// Shared RFC 7807 ProblemDetail shape + a translator that turns an
// errored fetch() Response into something the UI can render (banner,
// toast, inline). The backend writes `user_action` + `help_link`
// into every 4xx/5xx body (WP P2.5-11, see ValidationProblemAdvice +
// the per-controller handlers).

export interface ProblemDetail {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  /** Localized one-sentence action hint — renders as the banner subtitle. */
  user_action?: string;
  /** Relative link into the help-center, e.g. `/help/multi-seat`. */
  help_link?: string;
  /** Optional field-level errors (validation advice). */
  errors?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface TranslatedError {
  /** The exact HTTP status if known, or 0 when the request never reached the server. */
  status: number;
  /** Short, human-readable summary — falls back to a generic message per status bucket. */
  title: string;
  /** Actionable sentence for the user — prefers user_action, then detail, then generic. */
  body: string;
  /** Optional help-center link (relative). */
  helpLink?: string;
  /** Raw body — useful for devtools / error-boundary logging. */
  raw: ProblemDetail;
}

/**
 * Best-effort JSON read of a ProblemDetail body. Non-JSON bodies (HTML 502
 * from the ingress, empty 504, etc.) are preserved as a synthetic object
 * so the UI can still render a sensible fallback.
 */
export async function readProblem(response: Response): Promise<ProblemDetail> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/problem+json") || contentType.includes("application/json")) {
    try {
      return (await response.json()) as ProblemDetail;
    } catch {
      // fall through to status-only
    }
  }
  return { status: response.status };
}

/** Map an errored response into a `TranslatedError` ready for the UI. */
export async function translateResponse(response: Response): Promise<TranslatedError> {
  const raw = await readProblem(response);
  return translate(raw, response.status);
}

/** Build a TranslatedError from an already-parsed ProblemDetail. */
export function translate(problem: ProblemDetail, statusFallback = 0): TranslatedError {
  const status = problem.status ?? statusFallback;
  return {
    status,
    title: problem.title ?? fallbackTitle(status),
    body: problem.user_action ?? problem.detail ?? fallbackBody(status),
    helpLink: typeof problem.help_link === "string" ? problem.help_link : undefined,
    raw: problem,
  };
}

/** Translate a thrown error (network failure, aborted) into the same shape. */
export function translateException(error: unknown): TranslatedError {
  return {
    status: 0,
    title: "Verbindung fehlgeschlagen",
    body:
      error instanceof Error && error.message
        ? error.message
        : "Die Anfrage konnte nicht gesendet werden. Bitte prüfe deine Internet-Verbindung.",
    raw: {},
  };
}

function fallbackTitle(status: number): string {
  if (status === 0) return "Verbindung fehlgeschlagen";
  if (status === 400) return "Ungültige Eingabe";
  if (status === 401) return "Anmeldung erforderlich";
  if (status === 402) return "Upgrade erforderlich";
  if (status === 403) return "Keine Berechtigung";
  if (status === 404) return "Nicht gefunden";
  if (status === 409) return "Konflikt";
  if (status === 415) return "Nicht unterstütztes Format";
  if (status === 422) return "Ungültige Eingabe";
  if (status === 429) return "Zu viele Anfragen";
  if (status === 503) return "Service nicht verfügbar";
  if (status >= 500) return "Serverfehler";
  if (status >= 400) return "Anfrage fehlgeschlagen";
  return "Unbekannter Fehler";
}

function fallbackBody(status: number): string {
  if (status === 429) return "Bitte warte kurz und versuche es erneut.";
  if (status === 401) return "Bitte melde dich an und versuche es erneut.";
  if (status === 403) return "Dein Account darf diese Aktion aktuell nicht ausführen.";
  if (status === 404) return "Die angeforderte Ressource existiert nicht mehr.";
  if (status === 503) return "Der Service ist kurzzeitig nicht erreichbar. Bitte später erneut versuchen.";
  if (status >= 500) return "Auf dem Server ist ein Fehler aufgetreten. Bitte später erneut versuchen.";
  return "Bitte prüfe deine Eingaben und versuche es erneut.";
}
